import { axiosSecuredInstance } from "configurations/axiosConfig";
import { SelectFieldOption } from "controls/global/select-field/SelectInput";
import { Endorsement } from "entities/UIModel/company/Endorsement";
import {
  createHook,
  createStore,
  StoreActionApi,
} from "react-sweet-state";
import { formatDateToString } from "utils/shared";
// import { formatDateToString } from "utils/shared";

type State = {
  endorsements: Record<string, Endorsement[] | undefined>;
};
type Actions = typeof actions;
type StoreApi = StoreActionApi<State>;

const getCacheKey = (
  companyId: string,
  formType: string,
  stateAbbr: string,
  formId?: string | undefined,
  effectiveDate?: Date | null,
) => `${companyId}|${formType}|${stateAbbr}|${formId}|${formatDateToString(effectiveDate)}`;

function toSelectFieldOption(
  endorsements: Endorsement[] | undefined
): SelectFieldOption[] {
  if (!endorsements) return [];

  return endorsements.map((e) => ({
    text: e.endorsementName,
    value: e.endorsementName,
    altEndorsementID: e.endorsementID,
    endorsementVersion: e.endorsementVersion,
    opaIdentifier: e.opaIdentifier,
  }));
}

const buildEndorsements = (response: any) => () => {
  const data: Endorsement[] = response.data?.map((d: any) => {
    return {
      endorsementID: d.endorsementID,
      endorsementName: d.endorsementName,
      endorsementVersion: parseInt(d.endorsementVersion),
      opaIdentifier: d.opaIdentifier,
      endorsementCurrent: d.endorsementCurrent,
      isSFE: d.isSFE,
      order: d.order,
      default: d.default,
      isValid: d.isValid,
    } as Endorsement;
  });

  return data;
};

const actions = {
  getEndorsements:
    (
      companyId: string,
      formType: string | undefined = "",
      stateAbbr: string,
      formId?: string | undefined,
      effectiveDate?: Date | undefined,
    ) =>
      async ({ getState, setState, dispatch }: StoreApi) => {
        // console.log("call getEndorsements");
        try {
          const key = getCacheKey(companyId, formType, stateAbbr, formId, effectiveDate);
          const { endorsements } = getState();
          // console.log("current state for endorsements: ", endorsements, " -key:", key);

          if (endorsements[key]) {
            return;
          }

          // Don't call this api when JacketFormType is undefined or null
          if (!formType) {
            return;
          }

          const params = {
            CompanyID: companyId,
            JacketFormType: formType,
            State: stateAbbr,
            FormID: formId,
            DefaultDate: formatDateToString(effectiveDate),
          };

          const response = await axiosSecuredInstance.get<Endorsement[]>('/Company/GetJacketEndorsements', { params });
          const data = dispatch(buildEndorsements(response));

          const { endorsements: newEndorsements } = getState();
          if (newEndorsements[key]) {
            // console.log("newEndorsement:", newEndorsements, "-key:", key);
            return;
          }

          const copyCache = { ...newEndorsements };
          copyCache[key] = data;

          // console.log("getEndorsements - copyCache:", copyCache);
          setState({ endorsements: copyCache });
        } catch (error) {
          console.error(error);
        }
      },
  validateEndorsementEffectiveDate:
    (
      companyId: string,
      jacketFormType: string,
      stateAbbr: string,
      endorsementId: string, 
      effectiveDate: Date,
      formId?: string,
    ) =>
      async ({ getState, setState, dispatch }: StoreApi) => {
        try {
          const cacheKey = `${companyId}|${jacketFormType}|${stateAbbr}|${endorsementId}|${effectiveDate}|${formId}`;
          const { endorsements } = getState();

          if (endorsements[cacheKey]) {
            return endorsements[cacheKey];
          }          

          const params = {
            CompanyID: companyId,
            JacketFormType: jacketFormType,
            State: stateAbbr,
            EndorsementID: endorsementId,
            DefaultDate: formatDateToString(effectiveDate),
            FormID: formId,
          };

          const response = await axiosSecuredInstance.get<Endorsement[]>('/Company/ValidateEndorsementEffectiveDate', { params });
          const data = dispatch(buildEndorsements(response));

          // const { endorsements: newEndorsements } = getState();
          // if (newEndorsements[cacheKey]) {
          //   return;
          // }

          const copyCache = { ...endorsements };
          copyCache[cacheKey] = data;

          // console.log("validateEndorsementEffectiveDate - copyCache:", copyCache);
          setState({ endorsements: copyCache });
          // console.log("validation result:", data);
          return data;

        } catch (error) {
          console.error(error);
        }
      },      
};

const Store = createStore<State, Actions>({
  initialState: {
    endorsements: {},
  },
  actions,
  name: "companyEndorsementContext",
});

const useEndorsementState = createHook(Store, {
  selector: ({ endorsements }: State, key: string) => ({
    nonShortFormEndorsements: endorsements[key]?.filter((e) => !e.isSFE),
    shortFormEndorsements: endorsements[key]?.filter((e) => e.isSFE),
    formEndorsements: endorsements[key],
  }),
});
const useEndorsementActions = createHook(Store, { selector: null });

export {
  getCacheKey,
  toSelectFieldOption, useEndorsementActions, useEndorsementState
};

